/**
 * OPTIMIZED ATC TOAST NOTIFICATION
 * - Shows toast immediately while cart updates in background
 * - No delays in MoonBundle drawer updates
 * - Coordinated with cart system via events
 */

(function initATCToast() {
  'use strict';

  const TOAST_VISIBLE_TIME = 7000;
  const SILENT_ATC_DURATION = 2000;
  const DRAWER_GUARD_INTERVAL = 10;
  
  let toastTimer = null;
  let guardTimer = null;
  let safetyTimer = null;
  let silentATC = false;
  let lastClickedProductCard = null;
  let pendingCartUpdate = null;
  
  const DRAWER_SELECTOR = '.moonbundle-cart-drawer';

  /* ----------------------------------
     Capture clicked product card
  ---------------------------------- */
  document.addEventListener('click', (e) => {
    const atcButton = e.target.closest(
      'button[name="add"], .product-form__submit, .quick-add__submit'
    );
    
    if (!atcButton) return;

    // Check if button is disabled or sold out
    if (atcButton.disabled || atcButton.classList.contains('sold-out')) {
      return;
    }

    const productCard = atcButton.closest(
      '.main-product-page, .main-collection-product-grid-item, .featured-product-main, .card-wrapper, .product-card'
    );

    if (productCard) {
      lastClickedProductCard = productCard;
    }

    // Start drawer guard IMMEDIATELY - prevents flash
    silentATC = true;
    startDrawerGuard();

    // Safety timeout in case cart update never fires
    clearTimeout(safetyTimer);
    safetyTimer = setTimeout(() => {
      if (silentATC) {
        console.warn('[ATC Toast] Safety timeout - releasing guard');
        silentATC = false;
        stopDrawerGuard();
        lastClickedProductCard = null;
      }
    }, 5000); // 5 second safety
  });

  /* ----------------------------------
     Drawer guard functions
  ---------------------------------- */
  function forceDrawerClosed() {
    const drawer = document.querySelector(DRAWER_SELECTOR);
    if (!drawer) return;

    // Force close via multiple methods
    if (drawer.classList.contains('open')) {
      drawer.classList.remove('open');
      drawer.removeAttribute('data-open');
    }

    document.body.classList.remove('overflow-hidden');
    document.body.style.overflow = '';
  }

  function startDrawerGuard() {
    stopDrawerGuard(); // Clear existing

    // Add CSS guard class
    const drawer = document.querySelector(DRAWER_SELECTOR);
    if (drawer) {
      drawer.classList.add('drawer-setup-guard');
    }

    // Aggressive interval to keep drawer closed
    guardTimer = setInterval(() => {
      if (!silentATC) {
        stopDrawerGuard();
        return;
      }
      forceDrawerClosed();
    }, DRAWER_GUARD_INTERVAL);
  }

  function stopDrawerGuard() {
    if (guardTimer) {
      clearInterval(guardTimer);
      guardTimer = null;
    }

    // Remove CSS guard
    const drawer = document.querySelector(DRAWER_SELECTOR);
    if (drawer) {
      drawer.classList.remove('drawer-setup-guard');
    }
  }

  /* ----------------------------------
     Toast notification
  ---------------------------------- */
  function showATCToast() {
    if (!lastClickedProductCard) return;

    const toast = lastClickedProductCard.querySelector('.atc-toast-notification');
    if (!toast) return;

    // Show toast
    toast.classList.add('is-visible');

    // Clear existing timer
    clearTimeout(toastTimer);
    
    // Auto-hide after duration
    toastTimer = setTimeout(() => {
      toast.classList.remove('is-visible');
    }, TOAST_VISIBLE_TIME);
  }

  /* ----------------------------------
     MoonBundle drawer integration
     - UPDATES IMMEDIATELY - no delays
  ---------------------------------- */
  function updateMoonBundleDrawer(cartData) {
    // Method 1: Direct API call if available
    if (typeof window.openMoonBundleCartDrawer === 'function') {
      // Just refresh the drawer content, don't open it
      if (typeof window.refreshMoonBundleCart === 'function') {
        window.refreshMoonBundleCart(cartData);
      }
    }

    // Method 2: Trigger custom event for MoonBundle to catch
    document.dispatchEvent(new CustomEvent('moonbundle:cart-updated', {
      detail: { 
        cartData: cartData,
        timestamp: Date.now()
      }
    }));

    // Method 3: Force update if MoonBundle has a specific method
    const moonBundleDrawer = document.querySelector(DRAWER_SELECTOR);
    if (moonBundleDrawer && typeof moonBundleDrawer.updateCart === 'function') {
      moonBundleDrawer.updateCart(cartData);
    }
  }

  function openMoonbundleDrawer() {
    silentATC = false;
    stopDrawerGuard();

    if (typeof window.openMoonBundleCartDrawer === 'function') {
      window.openMoonBundleCartDrawer();
    }
  }

  /* ----------------------------------
     Toast action buttons
  ---------------------------------- */
  function initToastActions() {
    document.querySelectorAll('.atc-toast-notification').forEach((toast) => {
      // Close button
      const closeBtn = toast.querySelector('[data-toast-close]');
      if (closeBtn) {
        closeBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          toast.classList.remove('is-visible');
        });
      }

      // View cart / checkout link
      const viewCartLink = toast.querySelector('[data-toast-link]');
      if (viewCartLink) {
        viewCartLink.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          toast.classList.remove('is-visible');
          openMoonbundleDrawer();
        });
      }
    });
  }

  /* ----------------------------------
     Initialize listeners
  ---------------------------------- */
  function initialize() {
    // Initialize toast buttons
    initToastActions();

    // Re-initialize when new content is loaded
    document.addEventListener('DOMContentLoaded', initToastActions);
    document.addEventListener('shopify:section:load', initToastActions);

    // If Dawn pub/sub is available
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      
      // Listen for cart updates from product form
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        // Only handle product-form initiated updates
        if (event?.source !== 'product-form') return;

        // Clear safety timeout - request succeeded
        clearTimeout(safetyTimer);

        // Store cart data for MoonBundle
        pendingCartUpdate = event.cartData;

        // Show toast immediately
        showATCToast();

        // Update MoonBundle drawer IMMEDIATELY - no delay
        if (pendingCartUpdate) {
          updateMoonBundleDrawer(pendingCartUpdate);
        }

        // Start silent period
        silentATC = true;
        startDrawerGuard();

        // End silent period after duration
        setTimeout(() => {
          silentATC = false;
          stopDrawerGuard();
          lastClickedProductCard = null;
          pendingCartUpdate = null;
        }, SILENT_ATC_DURATION);
      });
    }

    // DIRECT INTEGRATION: Listen for cart section updates
    // This ensures MoonBundle updates even if pub/sub fails
    document.addEventListener('cart:section-updated', (event) => {
      // If we're in silent ATC mode, update MoonBundle immediately
      if (silentATC && event.detail?.cartData) {
        updateMoonBundleDrawer(event.detail.cartData);
      }
    });

    // Also listen for raw cart updates from Shopify
    document.addEventListener('cart:updated', (event) => {
      if (silentATC && event.detail?.cart) {
        updateMoonBundleDrawer(event.detail.cart);
      }
    });

    console.log('[ATC Toast] Initialized with zero-delay MoonBundle updates');
  }

  // Start initialization
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }
})();

/**
 * OPTIMIZED MOONBUNDLE CART BUBBLE SYNC
 * - No debounce delays
 * - Direct cart data passthrough
 * - Single source of truth
 */
(function initMoonBundleSync() {
  'use strict';

  function syncCartWithMoonBundle() {
    // Get cart data from the source of truth
    fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(cart => {
        // Update bubble count
        const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
        const countSpan = bubble?.querySelector('span[aria-hidden="true"]');
        const visuallyHiddenSpan = bubble?.querySelector('.visually-hidden');
        
        if (bubble && countSpan) {
          const itemCount = cart.item_count || 0;
          countSpan.textContent = itemCount;
          
          if (visuallyHiddenSpan) {
            visuallyHiddenSpan.textContent = `${itemCount} item${itemCount !== 1 ? 's' : ''}`;
          }
          
          bubble.style.display = itemCount === 0 ? 'none' : 'flex';
        }

        // Check if MoonBundle cart is empty
        const cartBody = document.querySelector('.moonbundle-cart-body');
        const emptyCart = cartBody?.querySelector('.moonbundle-cart-empty');
        
        // Force bubble to 0 if MoonBundle shows empty cart
        if (emptyCart) {
          const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
          const countSpan = bubble?.querySelector('span[aria-hidden="true"]');
          if (bubble && countSpan && countSpan.textContent !== '0') {
            countSpan.textContent = '0';
            bubble.style.display = 'none';
          }
        }
      })
      .catch(() => {
        // Silent fail
      });
  }

  // Sync on cart updates via pub/sub
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, syncCartWithMoonBundle);
  }

  // Sync on MoonBundle drawer changes - NO DEBOUNCE
  const observer = new MutationObserver(() => {
    syncCartWithMoonBundle();
  });

  function startObserving() {
    const moonbundleDrawer = document.querySelector('.moonbundle-cart-drawer');
    if (moonbundleDrawer) {
      observer.observe(moonbundleDrawer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-open', 'class']
      });
    }
    syncCartWithMoonBundle();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startObserving);
  } else {
    startObserving();
  }

  // Fallback check - but with longer interval to avoid performance issues
  setInterval(syncCartWithMoonBundle, 3000);
})();